import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner, faPlay, faExclamationTriangle, faCheckCircle,
  faTimesCircle, faSearch, faShieldAlt, faHeartbeat,
  faMousePointer, faKeyboard, faListAlt, faSync, faCircle
} from '@fortawesome/free-solid-svg-icons';
import axios from '../../config/axiosConfig';

const EVENT_ICONS = {
  Click: faMousePointer,
  ValueChange: faKeyboard,
  SelectionChanged: faListAlt,
};

const VERDICT_COLORS = {
  DATA_RETURNED: { bg: 'bg-red-600', text: 'text-white', label: 'DATA RETURNED' },
  LARGE_RESPONSE: { bg: 'bg-orange-500', text: 'text-white', label: 'LARGE RESPONSE' },
  ERROR: { bg: 'bg-yellow-600', text: 'text-white', label: 'ERROR' },
  IGNORED: { bg: 'bg-green-600', text: 'text-white', label: 'IGNORED (protected)' },
  MINIMAL: { bg: 'bg-green-700', text: 'text-white', label: 'MINIMAL' },
  NO_RESPONSE: { bg: 'bg-gray-500', text: 'text-white', label: 'NO RESPONSE' },
  NO_EVENTS: { bg: 'bg-gray-500', text: 'text-white', label: 'NO EVENTS' },
};

const SessionStatusDot = ({ status, isDarkMode }) => {
  if (status === 'checking') return <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400 text-[10px]" />;
  if (status === 'alive') return <FontAwesomeIcon icon={faCircle} className="text-green-500 text-[8px]" />;
  if (status === 'expired') return <FontAwesomeIcon icon={faCircle} className="text-red-500 text-[8px]" />;
  return <FontAwesomeIcon icon={faCircle} className="text-gray-500 text-[8px]" />;
};

const VwgAuthzTester = ({ webTarget, isDarkMode }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [baselineVwg, setBaselineVwg] = useState('');
  const [testVwg, setTestVwg] = useState('');
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [replayResults, setReplayResults] = useState(null);
  const [replayLoading, setReplayLoading] = useState(false);
  const [selectedControls, setSelectedControls] = useState(new Set());
  const [error, setError] = useState('');

  // Session status tracking
  const [sessionStatus, setSessionStatus] = useState({}); // vwg → 'alive'|'expired'|'checking'
  const [keepAliveActive, setKeepAliveActive] = useState(new Set());
  const keepAliveIntervals = useRef({});

  const cardBg = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = isDarkMode ? 'border-gray-700' : 'border-gray-200';

  const wtId = webTarget?._id;

  const loadSessions = useCallback(async () => {
    if (!wtId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`/vwg-authz/${wtId}/sessions`);
      setSessions(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [wtId]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Cleanup keep-alive intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(keepAliveIntervals.current).forEach(clearInterval);
    };
  }, []);

  const probeSession = async (vwg) => {
    setSessionStatus(prev => ({ ...prev, [vwg]: 'checking' }));
    try {
      const { data } = await axios.get(`/vwg-authz/${wtId}/probe/${vwg}`);
      setSessionStatus(prev => ({ ...prev, [vwg]: data.alive ? 'alive' : 'expired' }));
      return data;
    } catch {
      setSessionStatus(prev => ({ ...prev, [vwg]: 'expired' }));
      return { alive: false };
    }
  };

  const probeAll = async () => {
    const vwgs = [...new Set([baselineVwg, testVwg].filter(Boolean))];
    await Promise.all(vwgs.map(probeSession));
  };

  const toggleKeepAlive = (vwg) => {
    setKeepAliveActive(prev => {
      const next = new Set(prev);
      if (next.has(vwg)) {
        next.delete(vwg);
        if (keepAliveIntervals.current[vwg]) {
          clearInterval(keepAliveIntervals.current[vwg]);
          delete keepAliveIntervals.current[vwg];
        }
      } else {
        next.add(vwg);
        const send = async () => {
          try {
            const { data } = await axios.post(`/vwg-authz/${wtId}/keepalive/${vwg}`);
            setSessionStatus(p => ({ ...p, [vwg]: data.alive ? 'alive' : 'expired' }));
            if (!data.alive) {
              clearInterval(keepAliveIntervals.current[vwg]);
              delete keepAliveIntervals.current[vwg];
              setKeepAliveActive(p => { const n = new Set(p); n.delete(vwg); return n; });
            }
          } catch {
            setSessionStatus(p => ({ ...p, [vwg]: 'expired' }));
          }
        };
        send();
        keepAliveIntervals.current[vwg] = setInterval(send, 60000);
      }
      return next;
    });
  };

  const findNewSession = async () => {
    setError('');
    try {
      const { data } = await axios.get(`/vwg-authz/${wtId}/newest-session`, {
        params: { exclude: baselineVwg }
      });
      if (data.alive) {
        await loadSessions();
        setTestVwg(data.vwginstance);
        setSessionStatus(prev => ({ ...prev, [data.vwginstance]: 'alive' }));
      } else {
        setError('Keine aktive neue Session gefunden. Logge dich mit dem Low-Priv-User über den Proxy ein und versuche es nochmals.');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const runDiff = async () => {
    if (!baselineVwg || !testVwg) return;
    setDiffLoading(true);
    setDiff(null);
    setReplayResults(null);
    setSelectedControls(new Set());
    setError('');
    try {
      const { data } = await axios.post(`/vwg-authz/${wtId}/diff`, { baselineVwg, testVwg });
      setDiff(data);
      const autoSelect = new Set(
        data.baselineOnly
          .filter(c => c.events.some(e => ['Click', 'ValueChange', 'SelectionChanged'].includes(e.tp)))
          .map(c => c.controlId)
      );
      setSelectedControls(autoSelect);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setDiffLoading(false);
    }
  };

  const runReplay = async () => {
    if (!selectedControls.size) return;
    const testSession = sessions.find(s => s.vwginstance === testVwg);
    if (!testSession) return;
    setReplayLoading(true);
    setReplayResults(null);
    setError('');
    try {
      const { data } = await axios.post(`/vwg-authz/${wtId}/replay`, {
        baselineVwg,
        targetCookie: testSession.cookie,
        targetVwginstance: testVwg,
        controlIds: Array.from(selectedControls),
      });
      setReplayResults(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setReplayLoading(false);
    }
  };

  const toggleControl = (cid) => {
    setSelectedControls(prev => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };

  const selectAll = () => {
    if (!diff) return;
    setSelectedControls(new Set(diff.baselineOnly.map(c => c.controlId)));
  };

  const selectNone = () => setSelectedControls(new Set());

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl text-teal-500" />
      </div>
    );
  }

  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  };

  const sessionLabel = (s) => {
    const name = s.profileName ? s.profileName.replace(/^Auto:\s*/i, '') : s.vwginstance.substring(0, 8) + '…';
    return `${name} | ${s.controlCount ?? '?'} Ctrls | ${s.exchangeCount} Req | ${formatTime(s.firstSeen)}–${formatTime(s.lastSeen)}`;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className={`p-4 rounded-lg border ${cardBg}`}>
        <h3 className={`text-lg font-bold mb-1 ${textPrimary}`}>
          <FontAwesomeIcon icon={faShieldAlt} className="mr-2 text-teal-500" />
          VWG AuthZ Tester
        </h3>
        <p className={`text-sm ${textSecondary}`}>
          Vergleicht VWG-Control-Events zwischen Rollen und replayed Admin-Events durch Low-Priv-Sessions.
        </p>
      </div>

      {error && (
        <div className={`px-4 py-2 rounded text-sm ${isDarkMode ? 'bg-red-900/30 border border-red-700 text-red-300' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {error}
        </div>
      )}

      {/* Session Selector */}
      {sessions.length === 0 && !loading ? (
        <div className={`p-6 text-center rounded-lg border ${cardBg}`}>
          <p className={textSecondary}>Keine VWG-Sessions gefunden. Starte den Proxy und browse die App.</p>
        </div>
      ) : (
        <div className={`p-4 rounded-lg border ${cardBg}`}>
          <div className="flex items-center justify-between mb-3">
            <h4 className={`text-sm font-bold ${textPrimary}`}>Sessions auswählen</h4>
            <div className="flex gap-2">
              <button
                onClick={probeAll}
                disabled={!baselineVwg && !testVwg}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} disabled:opacity-50`}
              >
                <FontAwesomeIcon icon={faHeartbeat} /> Status prüfen
              </button>
              <button
                onClick={loadSessions}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                <FontAwesomeIcon icon={faSync} /> Aktualisieren
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Baseline (Admin) */}
            <div>
              <label className={`block text-xs font-semibold mb-1 ${textSecondary}`}>
                Baseline (Admin / High-Priv)
              </label>
              <div className="flex items-center gap-2">
                <select
                  value={baselineVwg}
                  onChange={e => { setBaselineVwg(e.target.value); setDiff(null); setReplayResults(null); }}
                  className={`flex-1 px-3 py-2 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                >
                  <option value="">— Session wählen —</option>
                  {sessions.map(s => (
                    <option key={s.vwginstance} value={s.vwginstance}>
                      {sessionLabel(s)}
                    </option>
                  ))}
                </select>
                {baselineVwg && <SessionStatusDot status={sessionStatus[baselineVwg]} isDarkMode={isDarkMode} />}
              </div>
              {baselineVwg && (
                <div className="mt-1 flex items-center gap-2">
                  <button
                    onClick={() => toggleKeepAlive(baselineVwg)}
                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                      keepAliveActive.has(baselineVwg)
                        ? 'bg-green-600 text-white'
                        : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    <FontAwesomeIcon icon={faHeartbeat} className="mr-1" />
                    {keepAliveActive.has(baselineVwg) ? 'Keep-Alive aktiv' : 'Keep-Alive'}
                  </button>
                  {sessionStatus[baselineVwg] === 'expired' && (
                    <span className="text-[10px] text-red-400">Session abgelaufen — Analyse funktioniert trotzdem (historische Daten)</span>
                  )}
                </div>
              )}
            </div>

            {/* Test (Low-Priv) */}
            <div>
              <label className={`block text-xs font-semibold mb-1 ${textSecondary}`}>
                Test (Low-Priv / Andere Rolle)
              </label>
              <div className="flex items-center gap-2">
                <select
                  value={testVwg}
                  onChange={e => { setTestVwg(e.target.value); setDiff(null); setReplayResults(null); }}
                  className={`flex-1 px-3 py-2 border rounded text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                >
                  <option value="">— Session wählen —</option>
                  {sessions.map(s => (
                    <option key={s.vwginstance} value={s.vwginstance}>
                      {sessionLabel(s)}
                    </option>
                  ))}
                </select>
                {testVwg && <SessionStatusDot status={sessionStatus[testVwg]} isDarkMode={isDarkMode} />}
              </div>
              <div className="mt-1 flex items-center gap-2">
                {testVwg && (
                  <button
                    onClick={() => toggleKeepAlive(testVwg)}
                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                      keepAliveActive.has(testVwg)
                        ? 'bg-green-600 text-white'
                        : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    <FontAwesomeIcon icon={faHeartbeat} className="mr-1" />
                    {keepAliveActive.has(testVwg) ? 'Keep-Alive aktiv' : 'Keep-Alive'}
                  </button>
                )}
                {sessionStatus[testVwg] === 'expired' && (
                  <button
                    onClick={findNewSession}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-orange-600 text-white hover:bg-orange-700"
                  >
                    <FontAwesomeIcon icon={faSync} className="mr-1" />
                    Neue Session nach Re-Login suchen
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={runDiff}
              disabled={!baselineVwg || !testVwg || baselineVwg === testVwg || diffLoading}
              className="bg-teal-600 text-white px-4 py-2 rounded text-sm hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
            >
              {diffLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : <FontAwesomeIcon icon={faSearch} />}
              Analysieren
            </button>
            {baselineVwg === testVwg && baselineVwg && (
              <span className={`text-xs ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                Baseline und Test müssen verschieden sein
              </span>
            )}
          </div>
        </div>
      )}

      {/* Diff Results */}
      {diff && (
        <div className={`p-4 rounded-lg border ${cardBg}`}>
          <div className="flex items-center gap-6 mb-4">
            <h4 className={`text-sm font-bold ${textPrimary}`}>Ergebnis</h4>
            <div className="flex gap-4 text-xs flex-wrap">
              <span className={`px-2 py-1 rounded ${isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-700'}`}>
                Nur Admin: {diff.summary.baselineOnlyCount}
              </span>
              <span className={`px-2 py-1 rounded ${isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-50 text-green-700'}`}>
                Gemeinsam: {diff.summary.sharedCount}
              </span>
              <span className={`px-2 py-1 rounded ${isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                Nur Test: {diff.summary.testOnlyCount}
              </span>
              <span className={`px-2 py-1 rounded font-semibold ${isDarkMode ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-50 text-orange-700'}`}>
                High-Interest: {diff.summary.highInterest}
              </span>
            </div>
          </div>

          {diff.baselineOnly.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-2">
                <h5 className={`text-xs font-semibold ${textSecondary}`}>
                  Admin-Only Controls (zum Replay auswählen)
                </h5>
                <div className="flex gap-2">
                  <button onClick={selectAll} className="text-xs text-teal-500 hover:underline">Alle</button>
                  <button onClick={selectNone} className="text-xs text-teal-500 hover:underline">Keine</button>
                </div>
              </div>
              <div className={`max-h-80 overflow-y-auto rounded border ${borderColor}`}>
                <table className="w-full text-xs">
                  <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                    <tr>
                      <th className="px-2 py-1.5 text-left w-8"></th>
                      <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Control ID</th>
                      <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Typ</th>
                      <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Events</th>
                      <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Label/Werte</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diff.baselineOnly.map(c => {
                      const hasInteresting = c.events.some(e =>
                        ['Click', 'ValueChange', 'SelectionChanged'].includes(e.tp)
                      );
                      return (
                        <tr
                          key={c.controlId}
                          onClick={() => toggleControl(c.controlId)}
                          className={`cursor-pointer border-t ${borderColor} ${
                            selectedControls.has(c.controlId)
                              ? isDarkMode ? 'bg-teal-900/30' : 'bg-teal-50'
                              : isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className="px-2 py-1.5 text-center">
                            <input
                              type="checkbox"
                              checked={selectedControls.has(c.controlId)}
                              onChange={() => toggleControl(c.controlId)}
                              className="rounded"
                            />
                          </td>
                          <td className={`px-2 py-1.5 font-mono ${hasInteresting ? 'font-bold text-orange-400' : textPrimary}`}>
                            SR={c.controlId}
                          </td>
                          <td className={`px-2 py-1.5 ${textSecondary}`}>
                            {c.controlType || '—'}
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="flex gap-1 flex-wrap">
                              {c.events.map(e => (
                                <span
                                  key={e.tp}
                                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                                    ['Click', 'ValueChange', 'SelectionChanged'].includes(e.tp)
                                      ? isDarkMode ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-100 text-orange-700'
                                      : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                                  }`}
                                >
                                  {EVENT_ICONS[e.tp] && <FontAwesomeIcon icon={EVENT_ICONS[e.tp]} className="text-[9px]" />}
                                  {e.tp} x{e.count}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className={`px-2 py-1.5 ${textSecondary} max-w-48 truncate`}>
                            {c.controlLabel || (c.txSamples.length > 0 ? c.txSamples[0].substring(0, 40) : '—')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Replay Button */}
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <button
                  onClick={runReplay}
                  disabled={!selectedControls.size || replayLoading}
                  className="bg-red-600 text-white px-4 py-2 rounded text-sm hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {replayLoading
                    ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    : <FontAwesomeIcon icon={faPlay} />
                  }
                  Replay {selectedControls.size} Controls durch Low-Priv-Session
                </button>
                {sessionStatus[testVwg] === 'expired' && (
                  <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'}`}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                    Test-Session abgelaufen — Replay wird fehlschlagen. Neu einloggen und "Neue Session suchen".
                  </span>
                )}
                {sessionStatus[testVwg] !== 'expired' && (
                  <span className={`text-xs ${textSecondary}`}>
                    Sendet Admin-Events mit den Credentials der Test-Session
                  </span>
                )}
              </div>
            </>
          )}

          {diff.baselineOnly.length === 0 && (
            <div className={`text-center py-6 ${textSecondary}`}>
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-2xl mb-2" />
              <p>Keine Admin-Only Controls gefunden — beide Sessions nutzen dieselben Controls.</p>
              <p className="text-xs mt-1">Das kann bedeuten: gleiche Berechtigungen, oder die Admin-Features wurden nicht besucht.</p>
            </div>
          )}
        </div>
      )}

      {/* Replay Results */}
      {replayResults && (
        <div className={`p-4 rounded-lg border ${cardBg}`}>
          <h4 className={`text-sm font-bold mb-3 ${textPrimary}`}>
            Replay-Ergebnisse
          </h4>
          <div className={`rounded border overflow-x-auto ${borderColor}`}>
            <table className="w-full text-xs">
              <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                <tr>
                  <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Control</th>
                  <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Event</th>
                  <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Status</th>
                  <th className={`px-2 py-1.5 text-right ${textSecondary}`}>Resp Bytes</th>
                  <th className={`px-2 py-1.5 text-right ${textSecondary}`}>Neue Controls</th>
                  <th className={`px-2 py-1.5 text-left ${textSecondary}`}>Verdict</th>
                </tr>
              </thead>
              <tbody>
                {replayResults.results.map((r, i) => {
                  const vd = VERDICT_COLORS[r.verdict] || VERDICT_COLORS.NO_RESPONSE;
                  const isVulnerable = r.verdict === 'DATA_RETURNED' || r.verdict === 'LARGE_RESPONSE';
                  return (
                    <tr
                      key={i}
                      className={`border-t ${borderColor} ${
                        isVulnerable
                          ? isDarkMode ? 'bg-red-900/20' : 'bg-red-50'
                          : ''
                      }`}
                    >
                      <td className={`px-2 py-1.5 font-mono ${textPrimary}`}>SR={r.controlId}</td>
                      <td className={`px-2 py-1.5 ${textSecondary}`}>
                        {EVENT_ICONS[r.eventType] && <FontAwesomeIcon icon={EVENT_ICONS[r.eventType]} className="mr-1" />}
                        {r.eventType}
                        {r.txValue ? ` "${r.txValue.substring(0, 20)}"` : ''}
                      </td>
                      <td className={`px-2 py-1.5 ${textSecondary}`}>{r.responseStatus || '—'}</td>
                      <td className={`px-2 py-1.5 text-right font-mono ${textSecondary}`}>
                        {r.responseLength != null ? r.responseLength.toLocaleString() : '—'}
                      </td>
                      <td className={`px-2 py-1.5 text-right font-mono ${textSecondary}`}>
                        {r.newControlCount != null ? r.newControlCount : '—'}
                      </td>
                      <td className="px-2 py-1.5">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${vd.bg} ${vd.text}`}>
                          {isVulnerable && <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />}
                          {r.verdict === 'IGNORED' && <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />}
                          {vd.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(() => {
            const vulnerable = replayResults.results.filter(r => r.verdict === 'DATA_RETURNED' || r.verdict === 'LARGE_RESPONSE');
            const protected_ = replayResults.results.filter(r => r.verdict === 'IGNORED' || r.verdict === 'MINIMAL');
            return (
              <div className="mt-3 flex gap-4 text-xs flex-wrap">
                {vulnerable.length > 0 && (
                  <span className={`px-3 py-1.5 rounded font-bold ${isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'}`}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                    {vulnerable.length} möglicherweise verwundbar
                  </span>
                )}
                {protected_.length > 0 && (
                  <span className={`px-3 py-1.5 rounded ${isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}>
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                    {protected_.length} geschützt
                  </span>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default VwgAuthzTester;
